export type QuadrantCount = {
  quadrant: string;
  count: number;
};

type Props = {
  data: QuadrantCount[];
  height?: number;
};

// Each cell in the 3×3 grid
type GridCell = {
  key: string;
  label: string;
  // Tailwind color classes: [bg-active, text-active, border-active, bg-empty, border-empty]
  colors: {
    bg: string;
    text: string;
    border: string;
    emptyBg: string;
    emptyBorder: string;
    countText: string;
  };
};

// Grid layout: row 0 = High potential (top), row 2 = Low potential (bottom)
//              col 0 = Low performance (left), col 2 = High performance (right)
// Colors unified with 9box.tsx QUADRANT_META — single source of truth.
const GRID: GridCell[][] = [
  // Row 0: High potential
  [
    {
      key: 'future-star',   // low perf, high pot
      label: 'Future Star',
      colors: {
        bg: 'bg-purple-50',
        text: 'text-purple-700',
        border: 'border-purple-300',
        emptyBg: 'bg-purple-50/40',
        emptyBorder: 'border-purple-200/60',
        countText: 'text-purple-800',
      },
    },
    {
      key: 'high-potential', // med perf, high pot
      label: 'High Potential',
      colors: {
        bg: 'bg-indigo-50',
        text: 'text-indigo-700',
        border: 'border-indigo-300',
        emptyBg: 'bg-indigo-50/40',
        emptyBorder: 'border-indigo-200/60',
        countText: 'text-indigo-800',
      },
    },
    {
      key: 'star',           // high perf, high pot
      label: 'Star',
      colors: {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-300',
        emptyBg: 'bg-amber-50/40',
        emptyBorder: 'border-amber-200/60',
        countText: 'text-amber-800',
      },
    },
  ],
  // Row 1: Medium potential
  [
    {
      key: 'question-mark',  // low perf, med pot
      label: 'Question Mark',
      colors: {
        bg: 'bg-orange-50',
        text: 'text-orange-700',
        border: 'border-orange-300',
        emptyBg: 'bg-orange-50/40',
        emptyBorder: 'border-orange-200/60',
        countText: 'text-orange-800',
      },
    },
    {
      key: 'average',        // med perf, med pot
      label: 'Average',
      colors: {
        bg: 'bg-gray-50',
        text: 'text-gray-600',
        border: 'border-gray-300',
        emptyBg: 'bg-gray-50/40',
        emptyBorder: 'border-gray-200/60',
        countText: 'text-gray-700',
      },
    },
    {
      key: 'high-performer', // high perf, med pot
      label: 'High Performer',
      colors: {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-300',
        emptyBg: 'bg-blue-50/40',
        emptyBorder: 'border-blue-200/60',
        countText: 'text-blue-800',
      },
    },
  ],
  // Row 2: Low potential
  [
    {
      key: 'low-performer',  // low perf, low pot
      label: 'Low Performer',
      colors: {
        bg: 'bg-rose-50',
        text: 'text-rose-700',
        border: 'border-rose-300',
        emptyBg: 'bg-rose-50/40',
        emptyBorder: 'border-rose-200/60',
        countText: 'text-rose-800',
      },
    },
    {
      key: 'underperformer', // med perf, low pot
      label: 'Underperformer',
      colors: {
        bg: 'bg-red-50',
        text: 'text-red-700',
        border: 'border-red-300',
        emptyBg: 'bg-red-50/40',
        emptyBorder: 'border-red-200/60',
        countText: 'text-red-800',
      },
    },
    {
      key: 'solid',          // high perf, low pot
      label: 'Solid',
      colors: {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-300',
        emptyBg: 'bg-emerald-50/40',
        emptyBorder: 'border-emerald-200/60',
        countText: 'text-emerald-800',
      },
    },
  ],
];

export function Box9QuadrantChart({ data, height = 200 }: Props) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center text-stone-400 text-sm" style={{ height }}>
        No grid data available
      </div>
    );
  }

  const countMap = Object.fromEntries(data.map((d) => [d.quadrant, d.count]));
  const total = data.reduce((s, d) => s + d.count, 0);

  return (
    <div className="flex flex-col gap-2" style={{ minHeight: height }}>
      {/* Axis label: Potential */}
      <div className="flex gap-1.5">
        {/* Y-axis label */}
        <div className="flex flex-col items-center justify-center w-4 shrink-0">
          <span
            className="text-[9px] font-medium text-stone-400 tracking-wide uppercase"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', lineHeight: 1 }}
          >
            Potential
          </span>
          <span className="text-[8px] text-stone-300 mt-1">↑</span>
        </div>

        {/* Grid */}
        <div className="flex flex-col gap-1 flex-1">
          {GRID.map((row, rowIdx) => (
            <div key={rowIdx} className="grid grid-cols-3 gap-1 flex-1">
              {row.map((cell) => {
                const count = countMap[cell.key] ?? 0;
                const isEmpty = count === 0;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;

                return (
                  <div
                    key={cell.key}
                    className={[
                      'relative rounded-md border flex flex-col items-center justify-center px-1 py-2 transition-all',
                      isEmpty
                        ? `${cell.colors.emptyBg} ${cell.colors.emptyBorder} border-dashed opacity-50`
                        : `${cell.colors.bg} ${cell.colors.border}`,
                    ].join(' ')}
                    style={{ minHeight: Math.floor((height - 32) / 3) }}
                    title={`${cell.label}: ${count} employee${count !== 1 ? 's' : ''}`}
                  >
                    <span
                      className={[
                        'text-[9px] font-medium leading-tight text-center',
                        isEmpty ? 'text-stone-300' : cell.colors.text,
                      ].join(' ')}
                    >
                      {cell.label}
                    </span>
                    <span
                      className={[
                        'text-xl font-bold leading-none mt-1',
                        isEmpty ? 'text-stone-200' : cell.colors.countText,
                      ].join(' ')}
                    >
                      {count}
                    </span>
                    {!isEmpty && total > 0 && (
                      <span className={`text-[9px] leading-none mt-0.5 opacity-60 ${cell.colors.text}`}>
                        {pct}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* X-axis label */}
      <div className="flex gap-1.5">
        <div className="w-4 shrink-0" />
        <div className="flex-1 flex items-center justify-center gap-1">
          <span className="text-[9px] text-stone-300">←</span>
          <span className="text-[9px] font-medium text-stone-400 tracking-wide uppercase">Performance</span>
          <span className="text-[9px] text-stone-300">→</span>
        </div>
      </div>
    </div>
  );
}
